import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StudentProfile,
  StudentFilters as StudentFiltersType,
  hasAcademyManagementRole,
  normalizeImportedStudentGender,
  TierFeatureKey,
} from '@court-sync/shared';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { Button } from '@/components/ui';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { DashboardStatCard } from '@/components/dashboard/DashboardStatCard';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { useTour } from '@/components/ui/Tour';
import { STUDENTS_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  STUDENTS_LIST_HELP,
  STUDENTS_LIST_TOUR_KEY,
  scheduleStudentsListTourStart,
} from '@/lib/tours/studentsManagementTour';
import { StudentFilters, AcademyStudentProfilesTable } from '../../../../components/students';
import { AddManualStudentModal } from '../../../../components/students/AddManualStudentModal';
import { NotifyStudentsModal } from '../../../../components/students/NotifyStudentsModal';
import { StudentClassSessionFilter } from '../../../../components/students/StudentClassSessionFilter';
import { StudentMessageHistoryDrawer } from '../../../../components/students/StudentMessageHistoryDrawer';
import { lessonSessionsService } from '../../../../services/lessonSessions';
import { AddStudentsToLessonModal } from '../../../../components/students/AddStudentsToLessonModal';
import { DeleteStudentConfirmDialog } from '../../../../components/students/DeleteStudentConfirmDialog';
import { academyStudentService } from '../../../../services/academyStudentService';
import { useAcademyStudentsPage, useAcademyStudentStats, usePrograms } from '../../../../hooks/useDataStore';
import { useAgeGroupLookups } from '../../../../queries/ageGroups';
import toast from 'react-hot-toast';
import {
  ChartLine,
  CalendarCheck,
  AlertCircle,
  User,
  Filter,
  Download,
  Upload,
  Search,
  Loader2,
  MoreVertical,
  Plus,
  X,
  ChevronDown,
} from 'lucide-react';
import { ImportModal, ImportRow } from '../../../../components/ImportModal';
import { downloadCSV } from '../../../../utils/csvUtils';
import { useAttendanceExportScope } from '../../../../hooks/useAttendanceExportScope';
import { buildAcademyAttendanceCsvRows } from '../../../../utils/attendanceCsvExport';

export default function StudentManagementPage() {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const navigate = useNavigate();

  const [studentPage, setStudentPage] = useState(1);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddManualStudentModal, setShowAddManualStudentModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyUseSessionId, setNotifyUseSessionId] = useState(false);
  const [sessionEnrollmentCount, setSessionEnrollmentCount] = useState<number | null>(null);
  const [showMessageHistory, setShowMessageHistory] = useState(false);
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [lessonEnrollStudentIds, setLessonEnrollStudentIds] = useState<string[]>([]);
  const [attendanceExportModalOpen, setAttendanceExportModalOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [deleteStudentTarget, setDeleteStudentTarget] = useState<StudentProfile | null>(null);
  const [deleteStudentLoading, setDeleteStudentLoading] = useState(false);
  const [filters, setFilters] = useState<StudentFiltersType>({
    sortBy: 'name',
    sortOrder: 'asc',
  });
  /** Local value for type-ahead; `filters.searchQuery` is debounced for server requests */
  const [searchInput, setSearchInput] = useState('');

  const {
    students,
    pagination: studentPagination,
    isLoading: studentsLoading,
    error,
    refresh: refreshStudents,
    invalidate: invalidateStudents,
  } = useAcademyStudentsPage(currentAcademy?.id, studentPage, filters);

  const {
    stats,
    isLoading: statsLoading,
    invalidate: invalidateStudentStats,
  } = useAcademyStudentStats(currentAcademy?.id);

  const { activeAgeGroups } = useAgeGroupLookups(currentAcademy?.id);
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { hasAccess: hasSmsNotificationsAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.SMS_NOTIFICATIONS
  );
  const smsAvailable = hasSmsNotificationsAccess && (currentAcademy?.smsEnabled ?? false);
  const { programs: cachedPrograms } = usePrograms(currentAcademy?.id, {
    enabled: !!currentAcademy?.id && hasProgramsAccess,
  });
  const programs = hasProgramsAccess ? cachedPrograms : [];

  const ageGroupOptions = useMemo(
    () => activeAgeGroups.map((g) => ({ id: g.id, label: g.label })),
    [activeAgeGroups]
  );

  const programOptions = useMemo(
    () => programs.filter((p) => p.isActive).map((p) => ({ id: p.id, name: p.name })),
    [programs]
  );

  const loading = studentsLoading || statsLoading;
  const showAddStudentHeaderAction = !loading && !error && students.length > 0;

  const refreshStudentData = useCallback(
    async (page = studentPage, options?: { force?: boolean }) => {
      if (page !== studentPage) {
        setStudentPage(page);
        return;
      }
      if (options?.force) {
        invalidateStudents();
        invalidateStudentStats();
      }
      await refreshStudents(options);
    },
    [studentPage, invalidateStudents, invalidateStudentStats, refreshStudents]
  );

  const searchPending = useMemo(() => {
    const draft = searchInput.trim();
    const applied = (filters.searchQuery ?? '').trim();
    return draft !== applied;
  }, [searchInput, filters.searchQuery]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      const next = trimmed === '' ? undefined : trimmed;
      setFilters((prev) => (prev.searchQuery === next ? prev : { ...prev, searchQuery: next }));
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);
  // Reset to first page when filters change (query key updates automatically).
  useEffect(() => {
    setStudentPage(1);
  }, [filters, currentAcademy?.id]);

  // Check access
  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
      return;
    }
    if (!currentAcademy) {
      navigate('/dashboard');
      return;
    }
  }, [user, currentAcademy, navigate]);

  const loadData = useCallback(
    (page = 1) => {
      void refreshStudentData(page, { force: true });
    },
    [refreshStudentData]
  );

  const handleStudentClick = useCallback(
    (student: StudentProfile) => {
      navigate(`/admin/students/${student.id}`);
    },
    [navigate]
  );

  const handleSendGuardianInvite = useCallback(
    async (student: StudentProfile) => {
      if (!currentAcademy?.id) return;
      const email = student.inviteEmail?.trim();
      if (!email) {
        toast.error('No invite email on file. Edit student details to add one first.');
        return;
      }
      try {
        const res = await academyStudentService.sendGuardianInvite(currentAcademy.id, student.id);
        if (res.success) {
          toast.success('Invitation email sent');
        } else {
          toast.error((res as { error?: string }).error || 'Failed to send invitation');
        }
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to send invitation');
      }
    },
    [currentAcademy?.id]
  );

  const handleResetFilters = () => {
    setSearchInput('');
    setFilters({
      sortBy: 'name',
      sortOrder: 'asc',
      searchQuery: undefined,
      skillLevel: undefined,
      listRecencyActive: undefined,
      ageMin: undefined,
      ageMax: undefined,
      ageGroupId: undefined,
      ballColor: undefined,
      programId: undefined,
      sessionId: undefined,
    });
  };

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setFilters((prev) => ({ ...prev, searchQuery: undefined }));
  }, []);

  const hasActiveFilters = Boolean(
    searchInput.trim() ||
    filters.skillLevel ||
    filters.listRecencyActive !== undefined ||
    filters.ageMin ||
    filters.ageMax ||
    filters.ageGroupId ||
    filters.ballColor ||
    filters.programId ||
    filters.sessionId
  );

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === students.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(students.map((s) => s.id)));
    }
  }, [students, selectedIds.size]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectAllEnrolledInSession = useCallback(async () => {
    if (!filters.sessionId) return;
    const bookings = await lessonSessionsService.getSessionEnrollments(filters.sessionId);
    const ids = [...new Set(bookings.map((b) => b.studentId).filter(Boolean))] as string[];
    setSelectedIds(new Set(ids));
  }, [filters.sessionId]);

  useEffect(() => {
    if (!filters.sessionId) {
      setSessionEnrollmentCount(null);
      return undefined;
    }
    let cancelled = false;
    void lessonSessionsService.getSessionEnrollments(filters.sessionId).then((bookings) => {
      if (cancelled) return;
      const ids = new Set(bookings.map((b) => b.studentId).filter(Boolean));
      setSessionEnrollmentCount(ids.size);
    });
    return () => {
      cancelled = true;
    };
  }, [filters.sessionId]);

  const openAddToLessonModal = useCallback((studentIds: string[]) => {
    setLessonEnrollStudentIds(studentIds);
    setShowAddLessonModal(true);
  }, []);

  const closeAddToLessonModal = useCallback(() => {
    setShowAddLessonModal(false);
    setLessonEnrollStudentIds([]);
  }, []);

  const handleAddStudentToLesson = useCallback(
    (student: StudentProfile) => {
      openAddToLessonModal([student.id]);
    },
    [openAddToLessonModal]
  );

  const handleDeleteStudentRequest = useCallback((student: StudentProfile) => {
    setDeleteStudentTarget(student);
  }, []);

  const handleConfirmDeleteStudent = useCallback(async () => {
    if (!currentAcademy?.id || !deleteStudentTarget) return;
    setDeleteStudentLoading(true);
    try {
      const res = await academyStudentService.deleteAcademyStudent(
        currentAcademy.id,
        deleteStudentTarget.id
      );
      if (res.success) {
        toast.success(`${deleteStudentTarget.firstName} ${deleteStudentTarget.lastName} deleted`);
        setSelectedIds((prev) => {
          if (!prev.has(deleteStudentTarget.id)) return prev;
          const next = new Set(prev);
          next.delete(deleteStudentTarget.id);
          return next;
        });
        setDeleteStudentTarget(null);
        await refreshStudentData(studentPagination?.page || 1, { force: true });
      } else {
        toast.error((res as { error?: string }).error || 'Failed to delete student');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete student');
    } finally {
      setDeleteStudentLoading(false);
    }
  }, [
    currentAcademy?.id,
    deleteStudentTarget,
    refreshStudentData,
    studentPagination?.page,
  ]);

  const attendanceExportScope = useAttendanceExportScope(currentAcademy?.id);
  const [attendanceExporting, setAttendanceExporting] = useState(false);
  const [exportingStudents, setExportingStudents] = useState(false);

  const handleExportAcademyAttendance = async () => {
    if (!currentAcademy?.id) return;
    setAttendanceExporting(true);
    try {
      const res = await academyStudentService.getAttendanceExport(
        currentAcademy.id,
        attendanceExportScope.scope
      );
      if (!res.success || !res.data) {
        toast.error((res as { error?: string }).error || 'Failed to export attendance');
        return;
      }
      const rows = res.data;
      if (rows.length === 0) {
        toast.error('No attendance records for this scope.');
        return;
      }
      downloadCSV(
        `attendance-${currentAcademy.name?.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`,
        buildAcademyAttendanceCsvRows(rows)
      );
      toast.success(`Exported ${rows.length} session${rows.length !== 1 ? 's' : ''}`);
      setAttendanceExportModalOpen(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setAttendanceExporting(false);
    }
  };

  const handleExportStudents = async () => {
    if (!currentAcademy?.id || exportingStudents) return;
    setExportingStudents(true);
    try {
      const allStudents = await academyStudentService.fetchAllAcademyStudentsPaginated(
        currentAcademy.id,
        filters
      );
      if (allStudents.length === 0) {
        toast.error('No students to export.');
        return;
      }
      const exportData = allStudents.map((student) => ({
        firstName: student.firstName,
        lastName: student.lastName,
        dateOfBirth: student.dateOfBirth
          ? new Date(student.dateOfBirth).toISOString().split('T')[0]
          : '',
        gender: student.gender,
        skillLevel: student.skillLevel || '',
        medicalNotes: student.medicalNotes || '',
        emergencyContactName: student.emergencyContact?.name || '',
        emergencyContactPhone: student.emergencyContact?.phone || '',
        emergencyContactRelationship: student.emergencyContact?.relationship || '',
      }));
      downloadCSV(
        `students-${currentAcademy.name?.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`,
        exportData
      );
      toast.success(`Exported ${allStudents.length} student${allStudents.length !== 1 ? 's' : ''}`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExportingStudents(false);
    }
  };

  const handleImportStudents = async (rows: ImportRow[]) => {
    if (!currentAcademy?.id) return undefined;

    /** Spreadsheet placeholders for “empty” optional fields */
    const asOptionalText = (v: string | undefined): string => {
      const s = String(v ?? '').trim();
      if (!s || /^n\/?a$/i.test(s) || s === '-') return '';
      return s;
    };
    const optionalEmail = (v: string | undefined): string | undefined => {
      const s = asOptionalText(v);
      return s || undefined;
    };

    const processedRows = rows.flatMap(r => {
      if (!r.isValid) return [];
      const gender = normalizeImportedStudentGender(r.data.gender);
      if (!gender) return [];
      return [
        {
          firstName: (r.data.firstName || '').trim(),
          lastName: (r.data.lastName || '').trim(),
          dateOfBirth: asOptionalText(r.data.dateOfBirth),
          gender,
          skillLevel: r.data.skillLevel as 'beginner' | 'intermediate' | 'advanced' | undefined,
          medicalNotes: r.data.medicalNotes,
          guardianEmail: optionalEmail(r.data.guardianEmail),
          inviteEmail: optionalEmail(r.data.inviteEmail),
          emergencyContactName: r.data.emergencyContactName,
          emergencyContactPhone: r.data.emergencyContactPhone,
          emergencyContactRelationship: r.data.emergencyContactRelationship,
        },
      ];
    });

    try {
      const result = await academyStudentService.importStudents(currentAcademy.id, processedRows);

      if (result.success && result.data) {
        const { createdCount, failedCount } = result.data;
        loadData();
        if (createdCount > 0) {
          toast.success(`Created ${createdCount} student${createdCount !== 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}`);
        } else {
          toast.error(`Failed to create any students: ${failedCount} errors`);
        }
        return { created: createdCount, failed: failedCount };
      }
      toast.error(result.error || 'Failed to import students');
      return undefined;
    } catch (error: any) {
      toast.error(`Import failed: ${error.message}`);
      return undefined;
    }
  };

  const { isActive: isTourActive } = useTour();
  const { startTourWithCompletionKey } = usePageTourActions();
  const setShowAddManualStudentModalRef = useRef(setShowAddManualStudentModal);
  const setShowImportModalRef = useRef(setShowImportModal);

  useEffect(() => {
    setShowAddManualStudentModalRef.current = setShowAddManualStudentModal;
  }, [setShowAddManualStudentModal]);

  useEffect(() => {
    setShowImportModalRef.current = setShowImportModal;
  }, [setShowImportModal]);

  const launchStudentsListTour = useCallback(() => {
    return scheduleStudentsListTourStart(startTourWithCompletionKey, {
      onCloseOverlays: () => {
        setShowAddManualStudentModalRef.current(false);
        setShowImportModalRef.current(false);
      },
      onOpenAddStudent: () => setShowAddManualStudentModalRef.current(true),
      onCloseAddStudent: () => setShowAddManualStudentModalRef.current(false),
      onOpenImport: () => setShowImportModalRef.current(true),
      onCloseImport: () => setShowImportModalRef.current(false),
      onFinish: () => {
        setShowAddManualStudentModalRef.current(false);
        setShowImportModalRef.current(false);
      },
    });
  }, [startTourWithCompletionKey]);

  const studentsFormatGuide = (
    <div className="space-y-3 text-xs">
      <div>
        <p className="font-semibold mb-2">Required columns</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">firstName</code> — Student first name</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">lastName</code> — Student last name</li>
          <li>
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">gender</code> —{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">male</code>,{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">female</code>,{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">other</code> (or{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">m</code> /{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">f</code> /{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">o</code>), plus common synonyms for other such as{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">non-binary</code>,{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">NB</code>,{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">prefer not to say</code>
          </li>
        </ul>
      </div>
      <div>
        <p className="font-semibold mb-2">Optional columns</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">dateOfBirth</code> — YYYY-MM-DD (omit if unknown)</li>
          <li>
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">guardianEmail</code> — Optional. If it
            matches an existing profile for this academy, the student links to that account. If not, an invite is
            stored for when that person signs up (typical for juniors). Adults you only track on paper can leave this
            blank.
          </li>
          <li>
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">inviteEmail</code> — When{' '}
            <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">guardianEmail</code> is empty, optional email
            for signup (parent/guardian for a child, or the adult student themselves).
          </li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">skillLevel</code> — beginner, intermediate, or advanced</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">medicalNotes</code></li>
          <li>Emergency: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">emergencyContactName</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">emergencyContactPhone</code>, <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">emergencyContactRelationship</code></li>
        </ul>
      </div>
      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-gray-700 dark:text-gray-300">
        <strong>Example:</strong>
        <br />
        firstName,lastName,gender,dateOfBirth,guardianEmail
        <br />
        Jane,Doe,female,,parent@example.com
        <br />
        Alex,Doe,other,,parent@example.com
      </div>
    </div>
  );

  useAutoPageTour(STUDENTS_LIST_TOUR_KEY, Boolean(currentAcademy?.id) && !loading, {
    onStart: launchStudentsListTour,
  });

  if (!user || !currentAcademy) {
    return null;
  }

  const studentsListHelpTour = [
    {
      tourKey: STUDENTS_LIST_TOUR_KEY,
      label: STUDENTS_LIST_HELP.tourLabel,
      description: STUDENTS_LIST_HELP.tourDescription,
    },
  ];

  return (
    <div className="min-h-screen">
      <main className="contents">
        <PageHeader
          title="Students"
          help={
            <PageFeatureHelp title={STUDENTS_LIST_HELP.title} tours={studentsListHelpTour}>
              <p>{STUDENTS_LIST_HELP.description}</p>
            </PageFeatureHelp>
          }
          actions={
            showAddStudentHeaderAction ? (
              <Button
                id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.ADD_STUDENT}
                type="button"
                size="sm"
                className="w-full md:w-auto"
                onClick={() => setShowAddManualStudentModal(true)}
              >
                <Plus aria-hidden />
                Add student
              </Button>
            ) : undefined
          }
        />

        {/* Stats Overview */}
        {stats && (
          <div className="mb-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <DashboardStatCard
              icon={<User aria-hidden />}
              iconBg="bg-blue-100 dark:bg-blue-900"
              iconColor="text-blue-600 dark:text-blue-400"
              label="Active Students"
              mobileLabel="Active"
              value={stats.totalStudents}
            />
            <DashboardStatCard
              icon={<ChartLine aria-hidden />}
              iconBg="bg-green-100 dark:bg-green-900"
              iconColor="text-green-600 dark:text-green-400"
              label="Avg Attendance"
              mobileLabel="Avg %"
              value={`${stats.averageAttendanceRate}%`}
            />
            <DashboardStatCard
              icon={<CalendarCheck aria-hidden />}
              iconBg="bg-yellow-100 dark:bg-yellow-900"
              iconColor="text-yellow-600 dark:text-yellow-400"
              label="Perfect Attendance"
              mobileLabel="Perfect"
              value={stats.perfectAttendance}
            />
            <DashboardStatCard
              icon={<AlertCircle aria-hidden />}
              iconBg="bg-red-100 dark:bg-red-900"
              iconColor="text-red-600 dark:text-red-400"
              label="At Risk (<70%)"
              mobileLabel="At risk"
              value={stats.atRisk}
            />
          </div>
        )}

        {/* Filters toolbar */}
        <div
          id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.FILTERS}
          className="mb-6 flex flex-col gap-3 sm:gap-4"
        >
          <div className="relative min-w-0 w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by student name…"
              aria-label="Search students by name"
              aria-busy={searchPending}
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
            {searchPending ? (
              <span
                className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                aria-hidden
              >
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : searchInput ? (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="flex flex-col gap-3 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="inline-flex shrink-0 items-center gap-2"
                onClick={() => setFiltersModalOpen(true)}
              >
                <Filter className="h-4 w-4" aria-hidden />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs text-white">
                    •
                  </span>
                )}
              </Button>
              {filters.sessionId && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void selectAllEnrolledInSession()}
                >
                  Select all enrolled
                </Button>
              )}
              <span className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                {studentPagination ? studentPagination.total : students.length} student
                {(studentPagination ? studentPagination.total : students.length) !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowMessageHistory(true)}
              >
                Message history
              </Button>
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DATA_ACTIONS}
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  type="button"
                  aria-label="Student data actions"
                >
                  <MoreVertical className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onSelect={() => setAttendanceExportModalOpen(true)}>
                  <CalendarCheck className="h-4 w-4 mr-2" aria-hidden />
                  Attendance CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => void handleExportStudents()}
                  disabled={exportingStudents}
                >
                  {exportingStudents ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
                  ) : (
                    <Download className="h-4 w-4 mr-2" aria-hidden />
                  )}
                  {exportingStudents ? 'Exporting…' : 'Export'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setShowImportModal(true)}>
                  <Upload className="h-4 w-4 mr-2" aria-hidden />
                  Import
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </div>
        </div>

            <Dialog open={filtersModalOpen} onOpenChange={setFiltersModalOpen}>
              <DialogContent className="max-h-[min(90vh,800px)] max-w-lg gap-0 overflow-hidden p-0 sm:max-w-lg">
                <DialogHeader className="border-b border-border px-6 py-4 text-left">
                  <DialogTitle>Filter students</DialogTitle>
                  <DialogDescription>
                    Refine the roster by skill, program, age, or a specific class.
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[min(60vh,560px)] overflow-y-auto px-6 py-5">
                  <StudentFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    onReset={handleResetFilters}
                    hideSearch
                    ageGroups={ageGroupOptions}
                    programs={programOptions}
                  />
                  {currentAcademy && (
                    <StudentClassSessionFilter
                      academy={currentAcademy}
                      sessionId={filters.sessionId}
                      onSessionIdChange={(sessionId) =>
                        setFilters((prev) => ({ ...prev, sessionId }))
                      }
                    />
                  )}
                </div>
                <DialogFooter
                  className={`border-t border-border bg-muted/20 px-6 py-4 ${hasActiveFilters ? 'sm:justify-between' : 'sm:justify-end'}`}
                >
                  {hasActiveFilters ? (
                    <Button type="button" variant="ghost" onClick={handleResetFilters}>
                      Clear all
                    </Button>
                  ) : null}
                  <Button type="button" onClick={() => setFiltersModalOpen(false)}>
                    Done
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={attendanceExportModalOpen} onOpenChange={setAttendanceExportModalOpen}>
              <DialogContent className="max-w-md sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Export attendance history</DialogTitle>
                  <DialogDescription>
                    Optionally choose a term and/or a lesson or camp. Leave both as &quot;All&quot; to export every
                    session for this academy.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1">
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-foreground">Category (optional)</span>
                    <select
                      aria-label="Attendance export category filter"
                      value={attendanceExportScope.category}
                      onChange={(e) =>
                        attendanceExportScope.setCategory(
                          e.target.value as typeof attendanceExportScope.category
                        )
                      }
                      disabled={attendanceExportScope.loading}
                      className="rounded-md border border-input bg-background py-2 px-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <option value="">All lesson types</option>
                      <option value="term_group">Term group lessons</option>
                      <option value="private_in_term">Private in term</option>
                      <option value="private_standalone">Standalone private</option>
                      <option value="camp">Camps</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-foreground">Term (optional)</span>
                    <select
                      aria-label="Attendance export term filter"
                      value={attendanceExportScope.termId}
                      onChange={(e) => attendanceExportScope.setTermId(e.target.value)}
                      disabled={attendanceExportScope.loading}
                      className="rounded-md border border-input bg-background py-2 px-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <option value="">All terms</option>
                      {attendanceExportScope.terms.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-foreground">Lesson or camp (optional)</span>
                    <select
                      aria-label="Attendance export lesson filter"
                      value={attendanceExportScope.lessonId}
                      onChange={(e) => attendanceExportScope.setLessonId(e.target.value)}
                      disabled={attendanceExportScope.loading}
                      className="rounded-md border border-input bg-background py-2 px-2 text-sm text-gray-900 dark:text-gray-100"
                    >
                      <option value="">All in academy</option>
                      {attendanceExportScope.filteredLessons.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name} ({l.lessonType})
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <DialogFooter className="gap-2 sm:gap-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAttendanceExportModalOpen(false)}
                    disabled={attendanceExporting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={() => void handleExportAcademyAttendance()}
                    disabled={attendanceExporting || attendanceExportScope.loading}
                    className="inline-flex items-center justify-center gap-2"
                  >
                    {attendanceExporting ? (
                      <>
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                        Exporting…
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 shrink-0" aria-hidden />
                        Download CSV
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

        {/* Loading State */}
        {loading && !error && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <Button onClick={() => { loadData(); }} className="mt-4">
              Retry
            </Button>
          </div>
        )}

        <div id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.LIST}>
        {/* Empty State */}
        {!loading && !error && students.length === 0 && (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={User} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No students found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {hasActiveFilters
                ? 'Try adjusting your filters to find more students.'
                : 'Students will appear here once parents enroll their children in lessons.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button
                id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.ADD_STUDENT}
                type="button"
                onClick={() => setShowAddManualStudentModal(true)}
              >
                <Plus className="h-4 w-4 mr-2 shrink-0" aria-hidden />
                Add student
              </Button>
              {hasActiveFilters && (
                <Button variant="outline" onClick={handleResetFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          </DashboardEmptyStateCard>
        )}

        {!loading && !error && students.length > 0 && (
          <>
            <DashboardDataCard
              toolbar={
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedIds.size > 0
                      ? `${selectedIds.size} selected`
                      : 'Select rows to add to a lesson or send a notification.'}
                  </span>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={selectedIds.size === 0}
                          className="flex items-center gap-1.5"
                          aria-label="Bulk actions for selected students"
                        >
                          Bulk actions
                          <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[12rem]">
                        <DropdownMenuItem
                          onSelect={() => openAddToLessonModal([...selectedIds])}
                        >
                          Add to lesson
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => {
                            setNotifyUseSessionId(false);
                            setShowNotifyModal(true);
                          }}
                        >
                          Send notification
                        </DropdownMenuItem>
                        {filters.sessionId && (
                          <DropdownMenuItem
                            onSelect={() => {
                              setNotifyUseSessionId(true);
                              setShowNotifyModal(true);
                            }}
                          >
                            Notify all in this class
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={selectedIds.size === 0}
                      onClick={clearSelection}
                    >
                      Deselect all
                    </Button>
                  </div>
                </div>
              }
            >
                <AcademyStudentProfilesTable
                  variant="owner"
                  students={students}
                  pagination={studentPagination}
                  loading={loading}
                  emptyMessage="No students found."
                  onPageChange={(page) => setStudentPage(page)}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  onToggleSelectAll={toggleSelectAll}
                  onViewDetails={handleStudentClick}
                  onSendGuardianInvite={handleSendGuardianInvite}
                  onAddToLesson={handleAddStudentToLesson}
                  onDeleteStudent={handleDeleteStudentRequest}
                />
            </DashboardDataCard>
            <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span className="sr-only">Row labels:</span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                  aria-hidden
                >
                  Roster only
                </span>
                <span>
                  Staff-added or imported student with no invite email — academy roster tracking only.
                </span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                  aria-hidden
                >
                  Awaiting signup
                </span>
                <span>Invite email on file — they finish signup through the invitation link.</span>
              </span>
            </p>
          </>
        )}
        </div>

        <NotifyStudentsModal
          isOpen={showNotifyModal}
          onClose={() => {
            setShowNotifyModal(false);
            setNotifyUseSessionId(false);
          }}
          selectedCount={
            notifyUseSessionId ? (sessionEnrollmentCount ?? 0) : selectedIds.size
          }
          smsAvailable={smsAvailable}
          onSubmit={async ({ subject, message, inApp, email, sms }) => {
            if (!currentAcademy) return;
            const body = notifyUseSessionId && filters.sessionId
              ? {
                  sessionId: filters.sessionId,
                  subject,
                  message,
                  channels: { inApp, email, sms },
                }
              : {
                  studentIds: [...selectedIds],
                  subject,
                  message,
                  channels: { inApp, email, sms },
                };
            const res = await academyStudentService.notifyStudents(currentAcademy.id, body);
            if (res.success && res.data) {
              toast.success(
                `Sent: ${res.data.sentInApp} in-app, ${res.data.sentEmail} email, ${res.data.sentSms} SMS`
              );
              clearSelection();
            } else {
              toast.error((res as { error?: string }).error || 'Failed');
            }
          }}
        />
        {currentAcademy && (
          <StudentMessageHistoryDrawer
            academyId={currentAcademy.id}
            open={showMessageHistory}
            onClose={() => setShowMessageHistory(false)}
          />
        )}
        {currentAcademy && (
          <AddStudentsToLessonModal
            isOpen={showAddLessonModal}
            onClose={closeAddToLessonModal}
            academyId={currentAcademy.id}
            studentIds={lessonEnrollStudentIds}
            onDone={() => {
              const enrolledFromBulkSelection =
                lessonEnrollStudentIds.length > 0 &&
                lessonEnrollStudentIds.length === selectedIds.size &&
                lessonEnrollStudentIds.every((id) => selectedIds.has(id));
              if (enrolledFromBulkSelection) {
                clearSelection();
              }
              closeAddToLessonModal();
              loadData(studentPagination?.page || 1);
            }}
          />
        )}

        {/* Import Modal */}
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportStudents}
          title="Import Students"
          description="Upload a CSV file to import multiple students at once"
          formatGuide={studentsFormatGuide}
          importingLabel="Importing students…"
          panelId={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.IMPORT_MODAL}
          requiredColumns={['firstName', 'lastName', 'gender']}
          additionalRowError={(row) => {
            const raw = String(row.gender ?? '').trim();
            if (!raw) return undefined;
            if (!normalizeImportedStudentGender(row.gender)) {
              return `Invalid gender "${raw}". Use male, female, or other (or equivalents such as non-binary, NB).`;
            }
            return undefined;
          }}
        />

        {currentAcademy?.id ? (
          <AddManualStudentModal
            isOpen={showAddManualStudentModal}
            onClose={() => setShowAddManualStudentModal(false)}
            academyId={currentAcademy.id}
            showBackdrop={!isTourActive}
            onCreated={(studentId) => {
              toast.success('Student created');
              loadData(1);
              navigate(`/admin/students/${studentId}`);
            }}
          />
        ) : null}

        <DeleteStudentConfirmDialog
          open={deleteStudentTarget != null}
          onOpenChange={(open) => {
            if (!open && !deleteStudentLoading) setDeleteStudentTarget(null);
          }}
          studentName={
            deleteStudentTarget
              ? `${deleteStudentTarget.firstName} ${deleteStudentTarget.lastName}`
              : ''
          }
          onConfirm={handleConfirmDeleteStudent}
          loading={deleteStudentLoading}
        />
      </main>
    </div>
  );
}
